// Base imports
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';

import {
	UiNodeInputAttributes,
	UpdateRecoveryFlowBody,
	RecoveryFlow,
	ErrorBrowserLocationChangeRequired,
	RecoveryFlowState
} from '@ory/client';
import { AxiosError } from 'axios';
import MDTextFieldWithValidation from '@/components/Validation/MDTextFieldWithValidation';
import { ErrorBackend, validation } from '@/_helpers';
import { feApi } from '@/_helpers/ory';
import { setParam } from '@/_helpers/url';
import store from '@/store';
import template from './Forget.vue';

@Component({
	mixins: [template],
	components: {
		ValidationObserver,
		MDTextFieldWithValidation
	}
})
export default class Recovery extends Vue {
	emailSend: boolean = false;

	loading: boolean = false;

	flow: RecoveryFlow = null;

	get darkMode(): boolean {
		return store.state.darkMode;
	}

	form: UpdateRecoveryFlowBody = {
		csrf_token: '',
		email: '',
		method: 'code'
	};

	token: string = '';

	async mounted(): Promise<void> {
		await this.getFlow();

		this.loading = false;
	}

	async getFlow() {
		const flowID: string = this.$route.query.flow as string;
		let getNewFlowID: boolean = flowID === '' || flowID === undefined;

		await feApi
			.getRecoveryFlow({ id: flowID })
			.then((response) => {
				this.setFlow(response.data);
			})
			.catch((error: AxiosError) => {
				getNewFlowID = true;
			});

		if (getNewFlowID) {
			feApi
				.createBrowserRecoveryFlow()
				.then((response) => {
					this.setFlow(response.data);
				})
				.catch((error: AxiosError) => {
					switch (error.response.status) {
						case 400:
							// User is currently logged in. This should never happen
							break;
						default:
							break;
					}
				});
		}
	}

	setFlow(flow: RecoveryFlow): void {
		flow.ui.nodes.forEach((value, index, array) => {
			const node: UiNodeInputAttributes = value.attributes as UiNodeInputAttributes;
			switch (node.name) {
				case 'csrf_token':
					this.form.csrf_token = node.value;
					break;
				case 'method':
					this.form.method = node.value;
					break;
				default:
					break;
			}
		});

		if (flow.state === RecoveryFlowState.SentEmail) {
			this.emailSend = true;
		}

		if (flow.ui.messages?.length > 0) {
			switch (flow.ui.messages[0].id) {
				case 4000002:
					(this.$refs.form1 as any).setErrors(validation.getErrors(this.getIncorrectEmailPasswordErrors()));
					break;
				default:
					break;
			}
		}
		setParam('flow', flow.id);

		this.flow = flow;
	}

	getIncorrectEmailPasswordErrors(): ErrorBackend[] {
		return [
			{
				code: 'verification_code_expired',
				title: 'Verificatie code verlopen',
				detail: 'De verificatie code is verlopen',
				source: {
					pointer: 'verificationcode'
				},
				metadata: {
					min: 0,
					max: 0,
					length: 0,
					in: []
				}
			}
		];
	}

	setFlowError(error: any): void {
		switch (error.error.id) {
			case 'browser_location_change_required':
				this.setFlowErrorBrowserLocationChangeRequired(error);
				break;
			default:
				console.error('Unhandeld error ID: ', error.error.id);
				break;
		}
	}

	setFlowErrorBrowserLocationChangeRequired(error: ErrorBrowserLocationChangeRequired) {
		window.location.href = error.redirect_browser_to;
	}

	async send() {
		if (this.loading) return;

		const isValid = await (this.$refs.form as any).validate();
		if (!isValid) {
			return;
		}

		this.loading = true;

		await feApi
			.updateRecoveryFlow({ flow: this.flow.id, updateRecoveryFlowBody: this.form })
			.then(() => {
				this.emailSend = true;
			})
			.catch((error: AxiosError) => {
				switch (error.response.status) {
					case 400:
						this.setFlow(error.response.data);
						break;
					default:
						break;
				}
			});

		this.loading = false;
	}

	async submit() {
		if (this.loading) return;

		const isValid = await (this.$refs.form1 as any).validate();
		if (!isValid) {
			return;
		}

		this.loading = true;

		const form: UpdateRecoveryFlowBody = {
			code: this.token,
			csrf_token: this.form.csrf_token,
			method: this.form.method
		};

		await feApi
			.updateRecoveryFlow({ flow: this.flow.id, updateRecoveryFlowBody: form })
			.then((resp): void => {
				this.setFlow(resp.data);
			})
			.catch((err: AxiosError) => {
				switch (err.response?.status) {
					case 400:
						// Status code 400 implies the form validation had an error
						this.setFlow(err.response?.data);
						break;
					case 422:
						this.setFlowError(err.response?.data);
						break;
					default:
						break;
				}
			})
			.catch((err) => {
				console.error(err);
			});

		this.loading = false;
	}
}
